import React, { useState } from "react";

import styled from "styled-components";

import { chosenSites } from "app/common/services/chosenSites";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { hasDigitalMarketingPermission, hasMarketResearchPermission } from "app/components/SideBar/utils";
import { i18nFilter } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/ngFilters";
import {
    MetricTitle,
    NoData,
    TableLoader,
    SeeMore,
} from "app/pages/keyword-analysis/TotalTrafficPage/Components/UtilityComponents";
import { SourceType } from "app/pages/website-performance/components/SearchTraffic/WWOTopKeywords";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";

import { TableContainer, TitleContainer, ButtonContainer, CtaWrapper } from "../SearchOverviewTraffic/StyledComponent";

import {
    SearchOverviewTopKeywordTableComponentSingle,
    SearchOverviewOrganicCompetitors,
} from "./SearchOverviewTopKeywordTableComponentSingle";
import {
    SearchOverviewTrafficTableComponent,
    SearchOverviewTrafficTableForSearchEnginesComponent,
} from "./SearchOverviewTrafficTableComponent";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";

const TableRowsAmount = 5;

const CardWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

export enum selectedTabTypes {
    organic = "organic",
    paid = "paid",
}

const TrafficBySearchEngine = (props) => {
    const HeadLineKey = "analysis.source.search.overview.byengines";
    const KeywordTooltipKey = "analysis.source.search.overview.byengines.tooltip";
    const SitesEndPoint = "widgetApi/TrafficSourcesSearch/SearchTrafficByEngines/Table";
    const chosenItems = chosenSites.sitelistForLegend();
    const addToDashboardParams = { key: chosenItems };

    const Constants = {
        SitesEndPoint,
        addToDashboardParams,
        HeadLineKey,
        KeywordTooltipKey,
        metricData: "SearchTrafficByEngines",
    };
    const renderTable = (props) => <SearchOverviewTrafficTableForSearchEnginesComponent {...props} />;
    return (
        <SearchOverviewTrafficTables
            {...props}
            {...Constants}
            renderTable={renderTable}
        />
    );
};

const TrafficBySearchType = (props) => {
    const HeadLineKey = "analysis.source.search.overview.bychannels";
    const KeywordTooltipKey = "analysis.source.search.overview.bychannels.tooltip";
    const SitesEndPoint = "widgetApi/TrafficSourcesSearch/SearchTrafficByChannel/Table";
    const chosenItems = chosenSites.sitelistForLegend();
    const addToDashboardParams = { key: chosenItems };
    const Constants = {
        addToDashboardParams,
        SitesEndPoint,
        HeadLineKey,
        KeywordTooltipKey,
        metricData: "SearchTrafficByChannel",
    };
    return (
        <SearchOverviewTrafficTables
            {...props}
            {...Constants}
        />
    );
};

const OrganicCompetitors = (props) => {
    const { from, to } = DurationService.getDurationData("3m").forAPI;
    const queryParams = { ...props.queryParams, from, to };
    const routingParams = { ...props.routingParams, duration: "3m" };
    const chosenItems = chosenSites.sitelistForLegend();
    const addToDashboardParams = { duration: "3m", key: chosenItems };
    const HeadLineKey = "search.overview.top.organic.competitors";
    const SeeAllKey = "analysis.source.search.overview.kwoverlap.seeMore";
    const SitesEndPoint = "widgetApi/SearchCompetitors/SearchCompetitorsOrganic/Table";
    const InnerLinkPage = "organicsearch_websites-competitorsKeywords";
    const ComponentName = "CompetitiveOverlap";
    const noDataMessage = "search.overview.organic.competitors.no.data.sub.title";

    const Constants = {
        SitesEndPoint,
        HeadLineKey,
        InnerLinkPage,
        ComponentName,
        SeeAllKey,
        metricData: "SearchCompetitorsOrganic",
        noDataMessage,
        addToDashboardParams,
    };
    const renderTable = (props) => <SearchOverviewOrganicCompetitors {...props} />;
    return (
        <SearchOverviewTrafficTables
            queryParams={queryParams}
            routingParams={routingParams}
            {...Constants}
            renderTable={renderTable}
        />
    );
};

export const SearchOverviewTopOrganicKeywords = (props) => {
    const routingParams = {
        ...props.routingParams,
        selectedTab: "keywords",
        SourceType: SourceType.ORGANIC,
        selectedPageTab: EKeywordTabs[EKeywordTabs.Organic],
    };
    const queryParams = {
        ...props.queryParams,
        SourceType: SourceType.ORGANIC,
        pageSize: TableRowsAmount,
    };
    const HeadLineKey = "search.overview.topKeyword.organic.title";
    const SeeAllKey = `search.overview.topKeyword.organic.see.all`;
    const KeywordTooltipKey = "analysis.source.search.overview.topKeyword.organic.single.tooltip";
    const SitesEndPoint = "widgetApi/SearchKeywordsV2/Table";
    const InnerLinkPage = "organicsearch_websites_v2_traffic_keywords";
    const selectedTab = selectedTabTypes.organic;
    const ComponentName = "TopOrganicKeywords";
    const Constants = {
        SitesEndPoint,
        HeadLineKey,
        KeywordTooltipKey,
        SeeAllKey,
        InnerLinkPage,
        ComponentName,
        selectedTab,
    };
    const renderTable = (props) => <SearchOverviewTopKeywordTableComponentSingle {...props} />;
    return (
        <SearchOverviewTrafficTables
            routingParams={routingParams}
            {...Constants}
            queryParams={queryParams}
            renderTable={renderTable}
        />
    );
};

export const SearchOverviewTopPaidKeywords = (props) => {
    const routingParams = {
        ...props.routingParams,
        selectedTab: "keywords",
        IncludePaid: true.toString(),
        selectedPageTab: EKeywordTabs[EKeywordTabs.Paid],
    };
    const queryParams = {
        ...props.queryParams,
        SourceType: SourceType.PAID,
        pageSize: TableRowsAmount,
    };
    const HeadLineKey = "search.overview.topKeyword.paid.title";
    const SeeAllKey = `search.overview.topKeyword.paid.see.all`;
    const KeywordTooltipKey = "analysis.source.search.overview.topKeyword.paid.single.tooltip";
    const SitesEndPoint = "widgetApi/SearchKeywordsV2/Table";
    const InnerLinkPage = "organicsearch_websites_v2_traffic_keywords";
    const selectedTab = selectedTabTypes.paid;
    const ComponentName = "TopPaidKeywords";
    const Constants = {
        SitesEndPoint,
        HeadLineKey,
        KeywordTooltipKey,
        InnerLinkPage,
        ComponentName,
        SeeAllKey,
        selectedTab,
    };
    const renderTable = (props) => <SearchOverviewTopKeywordTableComponentSingle {...props} />;
    return (
        <SearchOverviewTrafficTables
            routingParams={routingParams}
            {...Constants}
            queryParams={queryParams}
            renderTable={renderTable}
        />
    );
};

const SearchOverviewTrafficTablesInner = (props) => {
    const {
        queryParams,
        routingParams,
        SitesEndPoint,
        HeadLineKey,
        KeywordTooltipKey,
        SeeAllKey,
        InnerLinkPage,
        ComponentName,
        metricData,
        renderTable,
        noDataMessage = "search.overview.no.data.sub.title",
        addToDashboardParams,
    } = props;
    const [searchOverviewTrafficData, setSearchOverviewTrafficData] = useState({
        searchOverviewTrafficList: {},
        TotalCount: 0,
    });
    const { searchOverviewTrafficList } = searchOverviewTrafficData;
    const [isLoading, setIsLoading] = useState(true);

    const setData = async () => {
        const apiParams = { ...queryParams };
        const fetchService = DefaultFetchService.getInstance();
        const SitesDataPromise = fetchService.get(SitesEndPoint, apiParams);
        let SitesData;
        try {
            SitesData = await SitesDataPromise;
        } catch {
            setIsLoading(false);
        }
        const searchOverviewTrafficList = SitesData ? SitesData["Data"]?.slice(0, TableRowsAmount) : [];
        if (searchOverviewTrafficList.length === 0) {
            setIsLoading(false);
            return;
        }
        setSearchOverviewTrafficData({
            searchOverviewTrafficList: searchOverviewTrafficList.map((site) => {
                return { ...site };
            }),
            TotalCount: SitesData.TotalCount,
        });
        setIsLoading(false);
    };
    React.useEffect(() => {
        setData();
    }, [queryParams]);
    const i18n = i18nFilter();
    const innerLink = proNavigator.href(InnerLinkPage, {
        ...routingParams,
        IncludeNoneBranded: true,
    });
    const headline = i18n(HeadLineKey);
    const totalCount = numberFilter()(searchOverviewTrafficData.TotalCount);
    return (
        <CardWrapper>
            <TitleContainer>
                <MetricTitle
                    headline={headline}
                    tooltip={i18n(KeywordTooltipKey)}
                    fontSize={16}
                />
                {metricData ? (
                    <AddToDashboard
                        metric={metricData}
                        type="Table"
                        modelType=""
                        webSource={"Desktop"}
                        overrideAddToDashboardParams={addToDashboardParams}
                    />
                ) : null}
            </TitleContainer>
            {isLoading ? (
                <TableLoader />
            ) : !searchOverviewTrafficList || Object.values(searchOverviewTrafficList).length === 0 ? (
                <NoData
                    paddingTop={"50px"}
                    noDataTitleKey={"search.overview.no.data.title"}
                    noDataSubTitleKey={noDataMessage}
                />
            ) : (
                <>
                    <TableContainer>
                        {renderTable ? (
                            renderTable({ searchOverviewTrafficData, routingParams })
                        ) : (
                            <SearchOverviewTrafficTableComponent
                                searchOverviewTrafficData={searchOverviewTrafficData}
                                routingParams={routingParams}
                            />
                        )}
                    </TableContainer>
                    {InnerLinkPage && (hasMarketResearchPermission() || hasDigitalMarketingPermission()) ? (
                        <ButtonContainer>
                            <CtaWrapper>
                                <SeeMore
                                    componentName={ComponentName}
                                    innerLink={innerLink}
                                    textAlign={"end"}
                                >
                                    {i18n(SeeAllKey, { totalCount })}
                                </SeeMore>
                            </CtaWrapper>
                        </ButtonContainer>
                    ) : null}
                </>
            )}
        </CardWrapper>
    );
};

const propsAreEqual = (prevProps, nextProps) => prevProps.queryParams?.key === nextProps.queryParams?.key;
const SearchOverviewTrafficTables = React.memo(SearchOverviewTrafficTablesInner, propsAreEqual);
